import type { StudentsType } from "@/api_types/school";
import FetchService from "@/services/fetch.service";
import { asyncComputed } from "@/utils/asyncComputed";
import { sort } from "@/utils/sort";
import { defineStore } from "pinia";

export const useStudents = defineStore("directory/students", {
  state: () => {
    return {
      students: asyncComputed(
        async (controller) => {
          const res = (await FetchService.fetchData(
            "/students",
            "get",
            undefined,
            undefined,
            controller
          )) as StudentsType;

          return sort(res || [], (i) => i.child_fname + i.child_lname);
        },
        [],
        undefined,
        false,
        []
      )
    };
  },
  actions: {
    async getStudent(uid: string) {
      const res = await FetchService.fetchData(`/students/${uid}`, "get");
      return res;
    },
    async attach(student_uid: string, relationship: string, uid: string) {
              const res = await FetchService.fetchData(`/students/${student_uid}/attach`, "put", {
                [relationship]: uid
              });
              return res;
            },
    async detach(student_uid: string, relationship: string, uids: string | string[]) {
      const payload = Array.isArray(uids) ? { [relationship]: uids } : { [relationship]: [uids] };
      const res = await FetchService.fetchData(`/students/${student_uid}/detach`, "put", payload);
      return res;
    },
    async updateStudent(children_uid, studentData: Partial<StudentsType[number]>) {
      const res = await FetchService.fetchData(`/students/${children_uid}`, "put", studentData);

      if (res?.child_fname) {
        this.students.data.forEach((s) => {
          if (s.uid === children_uid) {
            Object.assign(s, res);
          }
        });
        return true;
      }
      return false;
    },
    /**
     * PUT /students/bulk-update body: { pinned: string[], link_household: string[] }
     * Both arrays must list every UID that should be true for that flag; omitted UIDs become false
     * on the server. Pinned is unchanged by this action — we send all currently pinned students.
     * Does not mutate local list; caller should reload students after the save flow (e.g.
     * `students.reload()` in the `saveLinkHouseholdBulk` finally block).
     */
    async bulkSetLinkHouseholdForFilter(
      filteredList: StudentsType[number][],
      target: boolean
    ): Promise<boolean> {
      const filteredUids = new Set(filteredList.map((s) => s.uid));
      const pinned = this.students.data.filter((s) => !!s.pinned).map((s) => s.uid);
      const link_household = this.students.data
        .filter((s) => (filteredUids.has(s.uid) ? target : !!s.link_household))
        .map((s) => s.uid);

      const res = await FetchService.fetchData("/students/bulk-update", "put", {
        pinned,
        link_household,
      });
      return res?.status === true;
    },
    async createStudent(studentData: Partial<StudentsType[number]>) {
      const res = await FetchService.fetchData(`/students`, "post", studentData);

      if (res?.child_fname) {
        res.deleted = false;
        this.students.data = sort(
          [...this.students.data, res],
          (i) => i.child_fname + i.child_lname
        );
        return true;
      }
      return false;
    }
  }
});

export default useStudents;
